import React, {useEffect, useRef, useState} from 'react';
import styled from 'styled-components';
import {useIsomorphicLayoutEffect} from '../../../hooks/useIsomorphicLayoutEffect/useIsomorphicLayoutEffect';

export interface CollapsibleProps {
  minHeight?: number;
  openerContent: React.ReactNode;
  bodyContent: React.ReactNode;
  className?: string;
  onChange?: (isOpen: boolean) => void;
  startCollapsed?: boolean;
  collapseOnClient?: boolean;
}

export const Collapsible: React.FC<CollapsibleProps> = ({
  openerContent,
  bodyContent,
  minHeight = DEFAULT_CONTENT_MIN_HEIGHT,
  onChange,
  className,
  startCollapsed = DEFAULT_IS_COLLAPSED,
  collapseOnClient,
}) => {
  const [isOpen, setIsOpen] = useState(!startCollapsed);
  const [height, setHeight] = useState(startCollapsed ? minHeight : undefined);
  const [shouldShowOpenerContent, setShouldShowOpenerContent] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  const handleOpen = () => {
    setIsOpen(!isOpen);
  };

  useIsomorphicLayoutEffect(() => {
    if (collapseOnClient && height === undefined) {
      setIsOpen(false);
    }
  });

  useEffect(() => {
    // eslint-disable-next-line local-rules/set-state-in-use-effect -- TODO: Cleanup setState in useEffect https://aboutyou.atlassian.net/browse/ASFE-7602
    setHeight(
      isOpen ? contentRef.current?.scrollHeight ?? undefined : minHeight,
    );
  }, [isOpen, minHeight]);

  useEffect(() => {
    onChange?.(isOpen);
  }, [isOpen, onChange]);

  useEffect(() => {
    if (contentRef.current) {
      // eslint-disable-next-line local-rules/set-state-in-use-effect
      setShouldShowOpenerContent(contentRef.current.scrollHeight > minHeight);
    }
  }, [contentRef.current?.scrollHeight, minHeight]);

  return (
    <Wrapper className={className}>
      <Body height={height}>
        <BodyWrap ref={contentRef}>{bodyContent}</BodyWrap>
      </Body>
      {shouldShowOpenerContent && (
        <Opener onClickCapture={handleOpen}>{openerContent}</Opener>
      )}
    </Wrapper>
  );
};

const Wrapper = styled.div`
  position: relative;
`;

const Opener = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const DEFAULT_CONTENT_MIN_HEIGHT = 80;

const DEFAULT_IS_COLLAPSED = true;

const Body = styled.div<{height?: number}>`
  display: flex;
  flex-direction: column;
  position: relative;
  height: ${({height}) => `${height}px`};
  // eslint-disable-next-line local-rules/css-layout-animation
  transition: height 0.3s ease-in-out;
  overflow: hidden;
`;

export const BodyWrap = styled.div`
  display: flex;
  flex-direction: column;
`;
